
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
  Bar,
} from "recharts";
import styles from "./RevenueTrendChart.module.css";
import { RxSlider } from "react-icons/rx";
import { SkeletonBarGroup } from "../Skeleton";
import { useMemo } from "react";
import { parseDateSafe } from "../../../utils/dateFormatters";

const RevenueTrendChart = ({ data = [], isLoading = false ,title}) => {
 

  // Sort data by month_date
  const sortedData = useMemo(() => {
    if (!data?.length) return [];
    return [...data].sort(
      (a, b) => (parseDateSafe(a.month_date) || new Date(0)) - (parseDateSafe(b.month_date) || new Date(0))
    );
  }, [data]);

  const formatCurrency = (value) => `$${Number(value || 0).toLocaleString()}`;

  // Custom tooltip shows all values, even 0
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const item = payload[0].payload;

    return (
      <div className={styles.tooltip}>
        <p className={styles.tooltipLabel}>{item.month}</p>
        <p className={styles.tooltipItem} style={{ color: "#4b5563" }}>
          RPM: {formatCurrency(item.rpm)}
        </p>
        <p className={styles.tooltipItem} style={{ color: "#3b82f6" }}>
          CCM: {formatCurrency(item.ccm)}
        </p>
        <p className={styles.tooltipItem} style={{ color: "#10b981", fontWeight: "bold" }}>
          Total: {formatCurrency(item.total)}
        </p>
      </div>
    );
  };

  const CustomLegend = ({ payload }) => (
    <div style={{ display: "flex", justifyContent: "center", gap: "1rem", paddingTop: "20px" }}>
      {payload.map((entry, index) => (
        <div key={index} style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontWeight: "bold" }}>
          <RxSlider color={entry.color} size={16} />
          <span style={{ color: entry.color, fontSize: "0.875rem" }}>{entry.value}</span>
        </div>
      ))}
    </div>
  );

  // Prepare data for line to start from Y-axis (0)
  const preparedData = useMemo(() => {
    if (!sortedData.length) return [];
    // Add a fake 0 point at the beginning if first month > 0
    const first = sortedData[0];
    const zeroPoint = {
      month: "",
      month_date: (() => { const d = parseDateSafe(first.month_date) || new Date(); return new Date(d.getFullYear(), d.getMonth() - 1, d.getDate()).toISOString(); })(),
      rpm: 0,
      ccm: 0,
      total: 0,
    };
    return [zeroPoint, ...sortedData];
  }, [sortedData]);

  const isAllZero = useMemo(() => {
    if (!sortedData.length) return false;
    return sortedData.every((item) => (item.rpm || 0) === 0 && (item.ccm || 0) === 0);
  }, [sortedData]);

  return (
    <div className={styles.chartContainer}>
       <h3 className={styles.chartTitle}>{title}</h3>
      {isLoading ? (
        <SkeletonBarGroup count={6} heights={["35%", "45%", "50%", "60%", "55%", "65%"]} />
      ) : !sortedData.length ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📊</div>
          <p className={styles.emptyText}>No revenue data available</p>
          <p className={styles.emptySubText}>Data will appear here once revenue is recorded</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <ComposedChart data={preparedData} margin={{ top: 20, right: 24, left: 24, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickFormatter={(value, index) => preparedData[index]?.month || ""}
            />

            <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} tickFormatter={formatCurrency} width={70} domain={[0, 'auto']} />

            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />

            {/* RPM Line */}
            <Line
              type="monotone"
              dataKey="rpm"
              name="RPM"
              stroke={isAllZero ? "transparent" : "#4b5563"}
              strokeWidth={3}
              dot={isAllZero ? false : { r: 5, fill: "#4b5563", strokeWidth: 2, stroke: "#fff" }}
              activeDot={isAllZero ? false : { r: 7 }}
              connectNulls={true}
            />

            {/* CCM Line */}
            <Line
              type="monotone"
              dataKey="ccm"
              name="CCM"
              stroke={isAllZero ? "#10b981" : "#3b82f6"}
              strokeWidth={3}
              dot={isAllZero ? { r: 5, fill: "#10b981", strokeWidth: 2, stroke: "#fff" } : { r: 5, fill: "#3b82f6", strokeWidth: 2, stroke: "#fff" }}
              activeDot={isAllZero ? { r: 7 } : { r: 7 }}
              connectNulls={true}
            />

            {/* Total as Bar */}
            <Bar dataKey="total" name="Total" barSize={8} fill="#10b981" />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default RevenueTrendChart;
